/** Production history API adapter: filters, search and resume preparation execute at the endpoint. */
import { HISTORY_CIPHERTEXT_MAX_BYTES } from '@viko/protocol'
import type { Fetcher } from './api.js'
import type { BrowserHistory, PrivateHistoryMetadata } from './encrypted-history.js'
import { shortCache } from './short-cache.js'
import type { HistoryRun, PreparedCommand, SearchHit } from './types.js'

/** read-budget: one metadata listing is shared by every history reader for this long. */
export const HISTORY_LIST_TTL_MS = 5_000

export function historyTransport(fetcher: Fetcher, origin: string) {
  async function request(operation: string, body: unknown): Promise<unknown> {
    const response = await fetcher(`${origin}/api/content/history/${operation}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok || !response.body) {
      await response.body?.cancel()
      throw new Error('Encrypted history unavailable')
    }
    const reader = response.body.getReader(),
      parts: Uint8Array[] = []
    let size = 0
    try {
      for (;;) {
        const part = await reader.read()
        if (part.done) break
        size += part.value.byteLength
        if (size > HISTORY_CIPHERTEXT_MAX_BYTES + 4096) {
          await reader.cancel()
          throw new Error('Encrypted history response too large')
        }
        parts.push(part.value)
      }
    } finally {
      reader.releaseLock()
    }
    const bytes = new Uint8Array(size)
    let offset = 0
    for (const part of parts) {
      bytes.set(part, offset)
      offset += part.length
    }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes))
  }
  return {
    list: (after?: string) => request('list', after ? { after } : {}),
    page: async (runId: string, after: number) => {
      const raw = (await request('read', { runId, after })) as { events?: unknown }
      if (!Array.isArray(raw?.events)) throw new Error('Invalid encrypted history page')
      return { events: raw.events }
    },
  }
}
export class PrivateHistory {
  private locked = false
  private readonly cache: ReturnType<typeof shortCache<PrivateHistoryMetadata[]>>
  constructor(
    readonly reader: BrowserHistory,
    o: { listTtlMs?: number; now?: () => number } = {},
  ) {
    this.cache = shortCache(o.listTtlMs ?? HISTORY_LIST_TTL_MS, o.now)
  }
  lock() {
    this.locked = true
    this.cache.clear()
    this.reader.lock()
  }
  /** New history may exist (an import finished): the next reader lists afresh. */
  changed() {
    this.cache.clearSettled()
  }
  async forget() {
    this.lock()
    await this.reader.forget()
  }
  private check() {
    if (this.locked) throw new Error('Encrypted history locked')
  }
  /** Every metadata row; concurrent and back-to-back readers share one listing (read-budget). */
  private async all(): Promise<PrivateHistoryMetadata[]> {
    this.check()
    const items = await this.cache('all', () => this.listAll())
    this.check()
    return items
  }
  private async listAll() {
    this.check()
    const items: PrivateHistoryMetadata[] = []
    let after: string | undefined
    for (let page = 0; page < 4096; page++) {
      const result = await this.reader.list(after)
      this.check()
      items.push(...result.items)
      if (!result.next) return items
      if (result.next === after) throw new Error('History cursor did not advance')
      after = result.next
    }
    throw new Error('Encrypted history listing limit')
  }
  private row(item: PrivateHistoryMetadata): HistoryRun {
    const r = item.run
    if (!item.machineId) throw new Error('History machine identity missing')
    return {
      runId: r.runId,
      machineId: item.machineId,
      agent: r.agent,
      status: r.source,
      projectId: r.projectId ?? '',
      title: r.title,
      headSeq: item.headSeq,
      startedAt: r.startedAt ?? 0,
      lastActivityAt: r.endedAt ?? r.startedAt ?? 0,
      sizeBytes: r.sizeBytes,
      model: r.model,
      continuedFrom: r.continuedFrom,
      source: r.source,
      readOnly: true,
      typingElsewhere: r.source === 'adopted' && r.live === true,
      nativeSessionId: r.nativeSessionId,
      cwd: r.cwd,
      gitBranch: r.gitBranch,
    }
  }
  async list(projectId?: string, q: { limit?: number; before?: number } = {}) {
    const rows = (await this.all())
      .map((item) => this.row(item))
      .filter(
        (r) =>
          (projectId === undefined || r.projectId === projectId) &&
          (q.before === undefined || r.startedAt < q.before),
      )
    return rows.sort((a, b) => b.lastActivityAt - a.lastActivityAt).slice(0, q.limit ?? rows.length)
  }
  async resume(
    runId: string,
    prompt = 'Continue this session.',
  ): Promise<{ command: PreparedCommand }> {
    const item = (await this.all()).find((r) => r.run.runId === runId)
    if (!item?.machineId) throw new Error('Encrypted history run not found')
    const r = item.run
    return {
      command: {
        kind: 'run.start',
        machineId: item.machineId,
        payload: {
          runId: crypto.randomUUID(),
          agent: r.agent,
          prompt,
          resumeNativeSessionId: r.nativeSessionId,
          ...(r.cwd ? { cwd: r.cwd } : {}),
          ...(r.projectId ? { projectId: r.projectId } : {}),
        },
      },
    }
  }
  async search(query: string, scope: { projectId?: string; source?: string; limit?: number } = {}) {
    const needle = query.trim().toLocaleLowerCase()
    if (!needle) return []
    const limit = Math.min(100, Math.max(1, scope.limit ?? 50)),
      hits: SearchHit[] = []
    const metadata = await this.all()
    for (const item of metadata) {
      const r = item.run
      if (
        (scope.projectId && r.projectId !== scope.projectId) ||
        (scope.source && r.source !== scope.source)
      )
        continue
      let after = 0
      while (after < item.headSeq) {
        const events = await this.reader.readEvents(item, after)
        this.check()
        if (!events.length) throw new Error('Encrypted history gap')
        for (const event of events) {
          const text =
            typeof (event as { text?: unknown }).text === 'string'
              ? (event as { text: string }).text
              : ''
          const at = text.toLocaleLowerCase().indexOf(needle)
          if (at >= 0)
            hits.push({
              run_id: r.runId,
              runId: r.runId,
              seq: event.seq,
              kind: event.kind,
              snippet: text.slice(Math.max(0, at - 80), at + needle.length + 160),
              eventId: event.eventId,
              ts: event.ts,
              machineId: item.machineId,
              agent: r.agent,
              source: r.source,
              projectId: r.projectId,
              title: r.title,
            })
          if (hits.length >= limit) return hits
          after = event.seq
        }
      }
    }
    return hits
  }
}
